import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import * as S from './ListWizardStepper.styles';

export type WizardStep = 1 | 2 | 3;

export type ListWizardStepperProps = {
  step: WizardStep;
  /** Edit: every step is a button. Add: only the steps behind the current one are; the current and the ones ahead are text. */
  allClickable: boolean;
  /** What a step behind reads in place of its name: step 1 the method ("Automatic" / "Hand-picked"), step 2 "{{n}} games". */
  summaries: { method: string; games: string };
  onStep: (step: WizardStep) => void;
  /** The second look (the `list-wizard-v2` flag): each step a pill, the current one filled, a chevron between them, and
   *  on a phone the current one across the row ("2 of 3") and the others their mark alone. */
  pills?: boolean;
  /** Whether the last step also picks the layout ("Name and layout"); else it is named "Name". */
  layoutStep?: boolean;
  /** The second step's name, already translated, for a flow whose second step is not the games ("Pick a collection");
   *  "Games" when not given. */
  gamesStepName?: string;
};

const STEPS: readonly WizardStep[] = [1, 2, 3];

/**
 * The list wizard's three steps: Method, Games, Name and layout. The current one is a filled number, one ahead an
 * outlined number, and one behind a check that reads its summary in place of its name ("Hand-picked", "3 games").
 * The marks are decoration (`aria-hidden`), so a step button is named by its words alone.
 */
export const ListWizardStepper = ({
  step,
  allClickable,
  summaries,
  onStep,
  pills = false,
  layoutStep = true,
  gamesStepName,
}: ListWizardStepperProps): React.ReactElement => {
  const { t } = useTranslation();
  // Literal t() per name, so the extractor harvests each.
  const name = (n: WizardStep): string =>
    n === 1 ? t('Method') : n === 2 ? gamesStepName ?? t('Games') : layoutStep ? t('Name and layout') : t('Name');
  const summary = (n: WizardStep): string => (n === 1 ? summaries.method : summaries.games);

  return (
    // The list role said outright: with its bullets styled away, Safari's VoiceOver no longer reads the <ol> as one.
    <S.Steps role="list" aria-label={t('Steps')} $pills={pills}>
      {STEPS.map((n) => {
        const current = n === step;
        const behind = n < step;
        const state: S.NumState = behind ? 'done' : current ? 'current' : 'ahead';
        // Already translated: the names above, the summaries by the caller.
        const words = behind ? summary(n) : name(n);
        const content = pills ? (
          <>
            <S.PillNum aria-hidden="true" $state={state}>
              {/* Keyed apart from the number, so the check mounts (and pops in) as the step falls behind. */}
              {behind ? (
                <S.PillCheck key="check" data-mark="done">
                  <Icon name="check" size={12} />
                </S.PillCheck>
              ) : (
                n
              )}
            </S.PillNum>
            <S.PillLabel $current={current} text={words} raw />
            {/* Seen on a phone only, where the other steps show their mark alone; the step's place is already said by
                aria-current. */}
            {current ? (
              <S.PillOf aria-hidden="true">{t('{{n}} of {{total}}', { n, total: STEPS.length })}</S.PillOf>
            ) : null}
          </>
        ) : (
          <>
            <S.Num aria-hidden="true" $state={state}>
              {behind ? <Icon name="check" size={11} /> : n}
            </S.Num>
            <S.Label $current={current} text={words} raw />
          </>
        );
        return (
          <S.Step key={n} $pills={pills} $grow={pills && current}>
            {pills && n > 1 ? (
              <S.Sep aria-hidden="true" data-step-sep="">
                <Icon name="chevron-down" size={16} />
              </S.Sep>
            ) : null}
            {allClickable || behind ? (
              <S.StepBtn
                type="button"
                data-step={n}
                $pills={pills}
                $state={state}
                aria-current={current ? 'step' : undefined}
                onClick={() => onStep(n)}
              >
                {content}
              </S.StepBtn>
            ) : (
              <S.StepText data-step={n} $pills={pills} $state={state} aria-current={current ? 'step' : undefined}>
                {content}
              </S.StepText>
            )}
          </S.Step>
        );
      })}
    </S.Steps>
  );
};

export default ListWizardStepper;
